<template>
  <b-container>
    <b-row v-if="aspect_id" class="mb-3">
      <b-col>
        <b-button :href="`/policy/new?aspect_id=${aspect_id}`" variant="secondary" size="sm">
          New Policy
        </b-button>
        <b-button variant="outline-secondary" size="sm" class="ml-2" @click="showLinkModal = true">
          Link Existing Policy
        </b-button>
      </b-col>
    </b-row>

    <b-modal v-model="showLinkModal" title="Link Existing Policy" size="lg" hide-footer>
      <b-form-input
        v-model="linkSearchTerm"
        placeholder="Search policies by number, name, or description..."
        @input="searchForLink"
        class="mb-3"
      ></b-form-input>
      <div v-if="linkSearching" class="text-center text-muted p-3">
        <b-spinner small class="mr-2"></b-spinner> Searching...
      </div>
      <div v-else-if="linkResults.length === 0 && linkSearchTerm" class="text-muted text-center p-3">
        No policies found.
      </div>
      <b-list-group v-else>
        <b-list-group-item
          v-for="p in linkResults"
          :key="p.id"
          class="d-flex justify-content-between align-items-center"
          style="cursor: pointer"
        >
          <div>
            <strong>{{ p.policyNumber }}</strong>
            <span v-if="p.description" class="text-muted ml-2">{{ p.description }}</span>
          </div>
          <b-button size="sm" variant="primary" @click="linkPolicy(p.id)">Link</b-button>
        </b-list-group-item>
      </b-list-group>
    </b-modal>
    <input
      v-if="!hideSearch"
      type="text"
      v-model="searchTerm"
      placeholder="Search policies"
      @input="fetchPolicies"
      class="form-control mb-3"
      id="policy_search"
    />

    <!-- Loading message -->
    <div v-if="loading" class="text-center text-muted p-4">
      <b-spinner small class="mr-2"></b-spinner>
      Searching policies, please wait...
    </div>

    <!-- No results message -->
    <div v-else-if="policies.length === 0" class="text-center text-muted p-4">
      <b-icon icon="inbox" font-scale="1.5"></b-icon>
      <div class="mt-2">No policies found.</div>
    </div>

    <!-- Table -->
    <table v-else class="table table-striped">
      <thead>
        <tr>
          <th>Policy</th>
          <th>Date Effective</th>
          <th>Label</th>
          <th>Policy Type</th>
          <th>Description</th>
          <th>Insurance Co</th>
          <th>Line of Coverage</th>
          <th>General Agent</th>
          <th>Broker</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="policy in policies" :key="policy.id">
          <td v-html="policy.html"></td>
          <td>{{ policy.dateEffective }}</td>
          <td>{{ policy.label }}</td>
          <td>{{ policy.policyType }}</td>
          <td>{{ policy.description }}</td>
          <td v-html="policy.insuranceCompany"></td>
          <td>{{ policy.lineOfCoverage }}</td>
          <td v-html="policy.generalAgent"></td>
          <td>{{ policy.broker }}</td>
        </tr>
      </tbody>
    </table>
  </b-container>
</template>



<script>
import PolicyDataService from '@/services/PolicyDataService';

export default {
  props: ["search_term", "aspect_id"],
  data() {
    return {
      searchTerm: '',
      policies: [],
      debounceTimeout: null,
      loading: false,
      showLinkModal: false,
      linkSearchTerm: '',
      linkResults: [],
      linkSearching: false,
      linkDebounce: null,
    };
  },
  computed: {
    hideSearch() {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return uuidRegex.test(this.searchTerm);
    },
  },
  methods: {
    fetchPolicies() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(async () => {
        this.loading = true;
        try {
          const response = await PolicyDataService.search(this.searchTerm);
          this.policies = (response.aaData || []).map((policy) => ({
            id: policy[2] || '',
            html: policy[0] || '',
            dateEffective: policy[1] || '',
            label: policy[4] || '',
            policyType: policy[5] || '',
            description: policy[6] || '',
            insuranceCompany: policy[7] || '',
            lineOfCoverage: policy[8] || '',
            generalAgent: policy[9] || '',
            broker: policy[10] || '',
          }));
        } catch (error) {
          console.error('Error fetching policies:', error);
          this.policies = [];
        } finally {
          this.loading = false;
        }
      }, 300);
    },
    searchForLink() {
      clearTimeout(this.linkDebounce);
      if (!this.linkSearchTerm || this.linkSearchTerm.length < 2) {
        this.linkResults = [];
        return;
      }
      this.linkDebounce = setTimeout(async () => {
        this.linkSearching = true;
        try {
          const response = await PolicyDataService.search(this.linkSearchTerm);
          this.linkResults = (response.aaData || []).map((p) => ({
            id: p[2] || '',
            policyNumber: (p[0] || '').replace(/<[^>]*>/g, ''),
            description: p[6] || '',
          }));
        } catch (e) {
          this.linkResults = [];
        } finally {
          this.linkSearching = false;
        }
      }, 300);
    },
    async linkPolicy(policyId) {
      try {
        await PolicyDataService.linkToAspect(policyId, this.aspect_id, 'Related');
        this.showLinkModal = false;
        this.linkSearchTerm = '';
        this.linkResults = [];
        this.fetchPolicies();
      } catch (e) {
        alert('Failed to link policy: ' + (e.message || 'Unknown error'));
      }
    },
  },
  mounted() {
    if (this.search_term) this.searchTerm = this.search_term;
    this.fetchPolicies();
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimeout);
  },
};

</script>
